import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'

import { App } from './App'
import { AuthProvider } from './auth/AuthProvider'
import { CrashFallback } from './components/CrashFallback'
import { initTelegramWebApp } from './telegram/tg'
import { IS_DEV } from './env'
import { initSentry, Sentry } from './sentry'
import { initOfflineSync } from './offline'
import { reloadOnceForStaleChunk } from './lazyReload'
import { prefetchManagerLanding } from './boot/prefetchLanding'
import './design/runtime' // sets window.__lang + the design-system config globals
import { LangProvider } from './i18n/lang'
import { ToastProvider } from './design'
import { applyMockTheme } from './dev/mock'

import './styles/styles.css'
import './styles/app.css'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      retry: (failureCount, error: unknown) => {
        const status = (error as { status?: number })?.status
        // Don't retry auth failures or any 5xx: retrying a saturated backend
        // just amplifies load (retry storm). Only retry transient network
        // errors (no status) once.
        if (status === 401 || status === 403) return false
        if (status && status >= 500) return false
        return failureCount < 1
      },
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
      // In the Telegram webview, focus fires on every keyboard/sheet toggle —
      // refetch-on-focus would storm CRM-proxied requests while users type.
      // The realtime WebSocket bridge + pull-to-refresh already cover liveness.
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
    },
  },
})

// Dev-only: `?mock_auth=1` skips real Telegram and lets us iterate UI
// without launching through @bitrixtsk_bot. The mock data must match
// brain.miniapp.find_memberships behaviour for the real backend.
function readMockInitData(): string | undefined {
  if (!IS_DEV) return undefined
  const params = new URLSearchParams(window.location.search)
  return params.get('mock_init_data') || undefined
}

// Vite's official recovery hook: a code-split chunk failed to load (stale hashes
// after a deploy). Reload once to fetch the current build before it can surface
// as a render crash. Belt-and-suspenders with lazyNamed's own .catch in App.tsx.
window.addEventListener('vite:preloadError', () => {
  reloadOnceForStaleChunk()
})

initSentry() // no-op unless VITE_SENTRY_DSN is set
initTelegramWebApp()
initOfflineSync() // replay any queued actions + drain on reconnect
applyMockTheme() // dev only: honor ?theme=dark after the SDK sets its own theme

// Warm the manager landing chunk now, in parallel with the /auth round-trip.
// Most users are managers; for a director it's one small wasted screen fetch
// from the CDN. The director chunk is warmed instead once auth confirms the
// role (AuthProvider), so we never pull the heavy director bundle for managers.
prefetchManagerLanding()

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={<CrashFallback />}>
      <QueryClientProvider client={queryClient}>
        <LangProvider>
          <ToastProvider>
            <AuthProvider mockInitData={readMockInitData()}>
              <App />
            </AuthProvider>
          </ToastProvider>
        </LangProvider>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  </StrictMode>,
)
